import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { IonContent, IonFooter, IonHeader, IonToolbar, ViewDidLeave } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { provideComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, Observable, tap } from 'rxjs';

import { LoadingComponent } from '@ninety/mobile/platform/components';
import { LanguageSelectors } from '@ninety/mobile/platform/state';
import { TerraStatusBadgeModule } from '@ninety/terra';

import { DiscussionFormMode } from '../../models/form/discussion-form.model';
import { DiscussionFormComponentActions as actions, DiscussionFormEffectsActions } from '../../state/actions';
import { DiscussionFormSelectors as selectors } from '../../state/selectors';
import { DiscussionFormsModule } from '../forms/discussion-forms.module';

import { DiscussionFormService } from './discussion-form.service';
import { DiscussionStepperComponentStore } from './discussion-stepper.store';

/**
 * A Component responsible for orchestrating the initialization of the
 * form and stepper, as well as presenting a UI for loading and error states.
 */
@Component({
  selector: 'nty-discussion-form-container',
  providers: [DiscussionFormService, provideComponentStore(DiscussionStepperComponentStore)],
  imports: [
    IonContent,
    IonFooter,
    IonHeader,
    IonToolbar,
    CommonModule,
    ReactiveFormsModule,
    LetDirective,
    DiscussionFormsModule,
    LoadingComponent,

    TerraStatusBadgeModule,
  ],
  templateUrl: 'discussion-form-container.component.html',
  styleUrls: ['discussion-form-container.component.scss'],
})
export class DiscussionFormContainerComponent implements OnInit, ViewDidLeave {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private destroyRef = inject(DestroyRef);
  public formService = inject(DiscussionFormService);
  public stepperStore = inject(DiscussionStepperComponentStore);

  ngOnInit() {
    this.store.dispatch(actions.discussionFormInit());
    this.initFormAndStepper();
    this.watchSaves();
  }

  ionViewDidLeave(): void {
    this.store.dispatch(actions.discussionFormDestroyed());
  }

  onSave(finished: boolean) {
    this.store.dispatch(actions.discussionFormSaved({ finished, formValue: this.formService.form.getRawValue() }));
  }

  readonly Mode = DiscussionFormMode;
  readonly vm$ = {
    discussion: this.store.select(selectors.selectFormDiscussion),
    questions: this.store.select(selectors.selectFormQuestions),
    mode: this.store.select(selectors.selectFormMode),
    loading: this.store.select(selectors.selectFormIsLoading),
    saving: this.store.select(selectors.selectFormIsSaving),
    language: this.store.select(LanguageSelectors.discussionLanguage),
    currentSectionNumber: this.stepperStore.current$,
    sectionCount: this.stepperStore.sectionCount$,
    readonly: this.store.select(selectors.selectFormIsReadOnly),
  };
  errorCount$: Observable<number> | null = null;
  questionCount$: Observable<number> | null = null;

  private initFormAndStepper() {
    this.actions$
      .pipe(
        ofType(DiscussionFormEffectsActions.discussionRequestSucceeded),
        concatLatestFrom(() => this.store.select(selectors.selectStepperAndFormData)),
        filter(([, { discussion }]) => !!discussion),
        tap(([, state]) => {
          if (state.discussion) this.formService.init({ ...state, discussion: state.discussion });
          this.stepperStore.init({ ...state, form: this.formService.form });
          this.errorCount$ = this.stepperStore.trackErrorsForCurrentSection(this.formService.form);
          this.questionCount$ = this.stepperStore.getTotalQuestionsForCurrentSection(
            this.formService.form,
            state.questions
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchSaves() {
    this.actions$
      .pipe(
        ofType(DiscussionFormEffectsActions.discussionFormSaveSucceeded),
        tap(() => this.formService.setSaved(true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
